import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  CheckCircle2,
  Clock,
  Filter,
  History,
  Loader2,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  Users,
  Trash2,
  Workflow,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  cancelAutomationRun,
  createAutomation,
  deleteAutomation,
  getAutomationAudience,
  getAutomationCatalog,
  getAutomationRuns,
  getAutomations,
  processDueAutomations,
  retryAutomationRun,
  runAutomationOverAudience,
  setAutomationStatus,
  updateAutomation,
} from '@/lib/api';

const inputClass = 'border-[rgba(6,43,26,0.16)] focus-visible:border-[#D4AF37] focus-visible:ring-[3px] focus-visible:ring-[#D4AF37]/25';

const EMPTY_FORM = {
  name: '',
  description: '',
  trigger_type: '',
  audience: '',
  trigger_statuses: [],
  wait_days: 7,
  conditions: [],
  template_id: '',
  sender: '',
  delay_hours: 0,
  repeat_policy: 'once_per_record',
};

// Status colours read at a glance: green is live, amber is holding, grey is
// retired. Draft deliberately looks inert — nothing about it has happened yet.
const STATUS_STYLES = {
  active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  paused: 'bg-amber-50 text-amber-800 border-amber-200',
  draft: 'bg-slate-50 text-slate-600 border-slate-200',
  archived: 'bg-slate-100 text-slate-500 border-slate-200',
};
const RUN_STATUS_STYLES = {
  sent: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  scheduled: 'bg-sky-50 text-sky-800 border-sky-200',
  failed: 'bg-red-50 text-red-800 border-red-200',
  skipped: 'bg-slate-50 text-slate-600 border-slate-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};
const RUN_STATUS_FILTERS = ['sent', 'scheduled', 'failed', 'skipped', 'cancelled'];

/**
 * The server explains its own refusals in sentences meant for staff, so those
 * are shown as-is. The fallback is only ever reached when the request never got
 * an answer at all.
 */
function errorText(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
}

function formatDateTime(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function StatCount({ label, value, tone }) {
  if (!value) return null;
  return (
    <span className={`rounded-md border px-2 py-0.5 text-[11px] font-medium ${tone}`}>
      {value} {label}
    </span>
  );
}

function EmptyState({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-dashed border-[#062B1A]/15 px-6 py-10 text-center">
      <Icon className="mx-auto mb-3 h-9 w-9 text-[#062B1A]/20" />
      <p className="font-medium text-[#062B1A]">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-[#062B1A]/50">{body}</p>
    </div>
  );
}

export default function AdminAutomations() {
  const [catalog, setCatalog] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [busyId, setBusyId] = useState('');

  const [runs, setRuns] = useState({ items: [], total: 0 });
  const [runsLoading, setRunsLoading] = useState(false);
  const [runSearch, setRunSearch] = useState('');
  const [runStatus, setRunStatus] = useState('all');
  const [runAutomationId, setRunAutomationId] = useState('all');
  const [busyRunId, setBusyRunId] = useState('');
  const [processing, setProcessing] = useState(false);

  // "Run now" is a two-step action on purpose: staff see who would be
  // contacted, and how many of them have already had this message, before
  // anything is sent.
  const [audience, setAudience] = useState(null);
  const [audienceFor, setAudienceFor] = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadAutomations = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (statusFilter === 'archived') params.include_archived = true;
      if (search.trim()) params.q = search.trim();
      const { data } = await getAutomations(params);
      setAutomations(data.items || []);
      setEmailConfigured(Boolean(data.email_configured));
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'Automations could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  const loadRuns = useCallback(async () => {
    try {
      setRunsLoading(true);
      const params = { limit: 50 };
      if (runStatus !== 'all') params.status = runStatus;
      if (runAutomationId !== 'all') params.automation_id = runAutomationId;
      if (runSearch.trim()) params.q = runSearch.trim();
      const { data } = await getAutomationRuns(params);
      setRuns({ items: data.items || [], total: data.total || 0 });
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'The automation history could not be loaded.'));
    } finally {
      setRunsLoading(false);
    }
  }, [runAutomationId, runSearch, runStatus]);

  useEffect(() => {
    getAutomationCatalog()
      .then(({ data }) => setCatalog(data))
      .catch((error) => {
        console.error(error);
        toast.error(errorText(error, 'The automation options could not be loaded.'));
      });
  }, []);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(loadAutomations, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [loadAutomations, search]);

  useEffect(() => {
    const timer = setTimeout(loadRuns, runSearch ? 300 : 0);
    return () => clearTimeout(timer);
  }, [loadRuns, runSearch]);

  const triggersByKey = useMemo(
    () => Object.fromEntries((catalog?.triggers || []).map((item) => [item.key, item])),
    [catalog],
  );
  const selectedTrigger = triggersByKey[form.trigger_type];
  const formAudience = selectedTrigger?.audience || form.audience || '';
  const conditionFields = catalog?.condition_fields?.[formAudience] || [];
  const availableTemplates = useMemo(
    () => (catalog?.templates || []).filter((item) => (item.audiences || []).includes(formAudience)),
    [catalog, formAudience],
  );

  const openNew = () => {
    setEditingId('');
    setForm(EMPTY_FORM);
    setEditorOpen(true);
  };

  const openEdit = (automation) => {
    setEditingId(automation.id);
    setForm({
      name: automation.name || '',
      description: automation.description || '',
      trigger_type: automation.trigger_type || '',
      audience: automation.audience || '',
      trigger_statuses: automation.trigger_statuses || [],
      wait_days: automation.wait_days || 7,
      conditions: (automation.conditions || []).map((item) => ({ ...item })),
      template_id: automation.template?.id || '',
      sender: automation.sender || '',
      delay_hours: automation.delay_hours || 0,
      repeat_policy: automation.repeat_policy || 'once_per_record',
    });
    setEditorOpen(true);
  };

  // Changing the trigger changes which audience the rule targets, which in turn
  // changes which conditions and templates are even valid. Clearing them here
  // is what stops a rule being saved against a field its audience does not have.
  const chooseTrigger = (triggerType) => {
    const trigger = triggersByKey[triggerType];
    setForm((current) => ({
      ...current,
      trigger_type: triggerType,
      audience: trigger?.audience || (trigger?.requires_audience ? current.audience : ''),
      trigger_statuses: [],
      conditions: [],
      template_id: '',
      repeat_policy: trigger?.kind === 'scheduled' ? 'once_per_record' : current.repeat_policy,
    }));
  };

  const chooseAudience = (audience) => {
    setForm((current) => ({ ...current, audience, conditions: [], template_id: '' }));
  };

  const toggleTriggerStatus = (value) => {
    setForm((current) => ({
      ...current,
      trigger_statuses: current.trigger_statuses.includes(value)
        ? current.trigger_statuses.filter((item) => item !== value)
        : [...current.trigger_statuses, value],
    }));
  };

  const addCondition = () => {
    const first = conditionFields[0];
    if (!first) return;
    setForm((current) => ({
      ...current,
      conditions: [...current.conditions, {
        field: first.key,
        operator: first.temporal ? 'older_than_days' : 'equals',
        value: '',
      }],
    }));
  };

  const updateCondition = (index, patch) => {
    setForm((current) => ({
      ...current,
      conditions: current.conditions.map((item, position) => {
        if (position !== index) return item;
        const next = { ...item, ...patch };
        if (patch.field) {
          const spec = conditionFields.find((field) => field.key === patch.field);
          // A date can only be compared by age, and nothing else can be.
          next.operator = spec?.temporal ? 'older_than_days' : 'equals';
          next.value = '';
        }
        return next;
      }),
    }));
  };

  const removeCondition = (index) => {
    setForm((current) => ({
      ...current,
      conditions: current.conditions.filter((_item, position) => position !== index),
    }));
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error('Give this automation a name so it can be recognised later.');
      return;
    }
    if (!form.trigger_type) {
      toast.error('Choose what starts this automation.');
      return;
    }
    if (selectedTrigger?.requires_audience && !form.audience) {
      toast.error('Choose which records this automation contacts.');
      return;
    }
    if (!form.template_id) {
      toast.error('Choose the email template this automation sends.');
      return;
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      trigger_type: form.trigger_type,
      audience: selectedTrigger?.requires_audience ? form.audience : null,
      trigger_statuses: selectedTrigger?.supports_status_filter ? form.trigger_statuses : [],
      wait_days: selectedTrigger?.requires_wait_days ? Number(form.wait_days) : null,
      conditions: form.conditions.map((item) => ({
        field: item.field, operator: item.operator, value: String(item.value ?? ''),
      })),
      template_id: form.template_id,
      sender: form.sender,
      delay_hours: Number(form.delay_hours) || 0,
      repeat_policy: form.repeat_policy,
    };

    try {
      setSaving(true);
      if (editingId) {
        await updateAutomation(editingId, payload);
        toast.success('Automation updated.');
      } else {
        await createAutomation(payload);
        toast.success('Automation saved as a draft. Activate it when you are ready.');
      }
      setEditorOpen(false);
      await loadAutomations();
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'This automation could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (automation, status) => {
    if (status === 'archived'
      && !window.confirm(`Archive “${automation.name}”? It will stop running and its history will be kept.`)) return;
    try {
      setBusyId(automation.id);
      const { data } = await setAutomationStatus(automation.id, status);
      const cancelled = data.cancelled_scheduled_runs || 0;
      toast.success(
        cancelled
          ? `${data.status_label}. ${cancelled} waiting message${cancelled === 1 ? ' was' : 's were'} stood down.`
          : `${automation.name} is now ${data.status_label.toLowerCase()}.`,
      );
      await loadAutomations();
      await loadRuns();
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'This automation could not be updated.'));
    } finally {
      setBusyId('');
    }
  };

  const remove = async (automation) => {
    if (!window.confirm(`Delete “${automation.name}”? This cannot be undone.`)) return;
    try {
      setBusyId(automation.id);
      await deleteAutomation(automation.id);
      toast.success('Automation deleted.');
      await loadAutomations();
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'This automation could not be deleted.'));
    } finally {
      setBusyId('');
    }
  };

  const retryRun = async (run) => {
    try {
      setBusyRunId(run.id);
      const { data } = await retryAutomationRun(run.id);
      if (data.status === 'sent') toast.success(`Sent to ${data.contact_name || data.contact_email}.`);
      else toast.error(data.outcome_message || 'The message could not be sent.');
      await loadRuns();
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'This message could not be retried.'));
    } finally {
      setBusyRunId('');
    }
  };

  const cancelRun = async (run) => {
    try {
      setBusyRunId(run.id);
      await cancelAutomationRun(run.id);
      toast.success('This message will not be sent.');
      await loadRuns();
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'This message could not be cancelled.'));
    } finally {
      setBusyRunId('');
    }
  };

  const openAudience = async (automation) => {
    setAudienceFor(automation);
    setAudience(null);
    try {
      setAudienceLoading(true);
      const { data } = await getAutomationAudience(automation.id);
      setAudience(data);
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'The audience for this automation could not be worked out.'));
      setAudienceFor(null);
    } finally {
      setAudienceLoading(false);
    }
  };

  const sendToAudience = async () => {
    if (!audienceFor) return;
    try {
      setSending(true);
      const { data } = await runAutomationOverAudience(audienceFor.id);
      const parts = [];
      if (data.sent) parts.push(`${data.sent} sent`);
      if (data.duplicate) parts.push(`${data.duplicate} already contacted`);
      if (data.failed) parts.push(`${data.failed} failed`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      toast.success(parts.length ? parts.join(', ') : 'Nobody matched this automation.');
      setAudienceFor(null);
      setAudience(null);
      await loadAutomations();
      await loadRuns();
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'This automation could not be run.'));
    } finally {
      setSending(false);
    }
  };

  const runChecks = async () => {
    try {
      setProcessing(true);
      const { data } = await processDueAutomations();
      const parts = [];
      if (data.sent) parts.push(`${data.sent} sent`);
      if (data.failed) parts.push(`${data.failed} failed`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      toast.success(parts.length ? `Checks finished — ${parts.join(', ')}.` : 'Checks finished. Nothing was due.');
      await loadRuns();
      await loadAutomations();
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'The scheduled checks could not be run.'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2
            className="flex items-center gap-2 text-2xl font-semibold text-[#062B1A]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            <Workflow className="h-6 w-6 text-[#D4AF37]" /> Automations
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-[#062B1A]/60">
            Rules that contact a lead, applicant or student on their own — when something happens,
            and only when your conditions are met.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={runChecks}
            disabled={processing}
            className="border-[#062B1A]/15 text-[#062B1A]"
          >
            {processing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Run checks now
          </Button>
          <Button onClick={openNew} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
            <Plus className="mr-2 h-4 w-4" /> New automation
          </Button>
        </div>
      </div>

      {/* Said out loud rather than discovered one failed run at a time. */}
      {!emailConfigured && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">Email sending is not configured on this server.</p>
            <p className="mt-0.5 text-amber-800">
              Automations will record every attempt as failed rather than pretending a message was delivered.
              Set the Gmail service account before activating a rule.
            </p>
          </div>
        </div>
      )}

      <Tabs defaultValue="rules">
        <TabsList className="bg-[#062B1A]/5">
          <TabsTrigger value="rules">Automations</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="mt-4 space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#062B1A]/35" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search automations by name"
                aria-label="Search automations by name"
                className={`pl-9 ${inputClass}`}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={`sm:w-52 ${inputClass}`} aria-label="Filter by status">
                <Filter className="mr-2 h-4 w-4 text-[#062B1A]/40" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All except archived</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 py-10 text-sm text-[#062B1A]/55">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading automations…
            </div>
          ) : automations.length === 0 ? (
            <EmptyState
              icon={Workflow}
              title={search || statusFilter !== 'all' ? 'No automations match this search' : 'No automations yet'}
              body={
                search || statusFilter !== 'all'
                  ? 'Try a different name or clear the status filter.'
                  : 'Create one to have the portal follow up on a new lead, a status change, or an application that has gone quiet.'
              }
            />
          ) : (
            <div className="space-y-3">
              {automations.map((automation) => {
                const stats = automation.stats || {};
                const isBusy = busyId === automation.id;
                return (
                  <Card key={automation.id} className="border border-[rgba(6,43,26,0.08)] shadow-sm">
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold text-[#062B1A]">{automation.name}</p>
                            <Badge
                              variant="outline"
                              className={`text-[11px] ${STATUS_STYLES[automation.status] || STATUS_STYLES.draft}`}
                            >
                              {automation.status_label}
                            </Badge>
                            {!automation.template.available && (
                              <Badge variant="outline" className="border-red-200 bg-red-50 text-[11px] text-red-800">
                                Template missing
                              </Badge>
                            )}
                          </div>
                          {automation.description && (
                            <p className="mt-1 text-sm text-[#062B1A]/60">{automation.description}</p>
                          )}

                          {/* The rule as a sentence, not as a schema. */}
                          <p className="mt-2 text-sm text-[#062B1A]/75">{automation.summary}</p>

                          <div className="mt-3 grid gap-x-6 gap-y-1 text-xs text-[#062B1A]/55 sm:grid-cols-2">
                            <p><span className="font-medium text-[#062B1A]/70">Audience:</span> {automation.audience_label}</p>
                            <p><span className="font-medium text-[#062B1A]/70">Template:</span> {automation.template.name || 'None'}</p>
                            <p><span className="font-medium text-[#062B1A]/70">Repeats:</span> {automation.repeat_label}</p>
                            <p><span className="font-medium text-[#062B1A]/70">Delay:</span> {automation.delay_label}</p>
                            {automation.updated_at && (
                              <p><span className="font-medium text-[#062B1A]/70">Updated:</span> {formatDateTime(automation.updated_at)}{automation.updated_by_name ? ` by ${automation.updated_by_name}` : ''}</p>
                            )}
                            {automation.last_triggered_at && (
                              <p><span className="font-medium text-[#062B1A]/70">Last ran:</span> {formatDateTime(automation.last_triggered_at)}</p>
                            )}
                          </div>

                          <div className="mt-3 flex flex-wrap gap-1.5">
                            <StatCount label="sent" value={stats.sent} tone="border-emerald-200 bg-emerald-50 text-emerald-800" />
                            <StatCount label="waiting" value={stats.scheduled} tone="border-sky-200 bg-sky-50 text-sky-800" />
                            <StatCount label="failed" value={stats.failed} tone="border-red-200 bg-red-50 text-red-800" />
                            <StatCount label="skipped" value={stats.skipped} tone="border-slate-200 bg-slate-50 text-slate-600" />
                            {!stats.sent && !stats.scheduled && !stats.failed && !stats.skipped && (
                              <span className="text-[11px] text-[#062B1A]/40">Has not run yet</span>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 lg:justify-end">
                          {automation.status === 'active' ? (
                            <Button
                              size="sm" variant="outline" disabled={isBusy}
                              className="border-amber-200 text-amber-800 hover:bg-amber-50"
                              onClick={() => changeStatus(automation, 'paused')}
                            >
                              <Pause className="mr-1.5 h-3.5 w-3.5" /> Pause
                            </Button>
                          ) : automation.status === 'archived' ? (
                            <Button
                              size="sm" variant="outline" disabled={isBusy}
                              className="border-[#062B1A]/15 text-[#062B1A]"
                              onClick={() => changeStatus(automation, 'paused')}
                            >
                              <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" /> Restore
                            </Button>
                          ) : (
                            <Button
                              size="sm" disabled={isBusy}
                              className="bg-emerald-700 text-white hover:bg-emerald-800"
                              onClick={() => changeStatus(automation, 'active')}
                            >
                              <Play className="mr-1.5 h-3.5 w-3.5" /> Activate
                            </Button>
                          )}
                          {automation.status !== 'archived' && (
                            <Button
                              size="sm" variant="outline" disabled={isBusy}
                              className="border-[#062B1A]/15 text-[#062B1A]"
                              onClick={() => openEdit(automation)}
                            >
                              <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                            </Button>
                          )}
                          {automation.status !== 'archived' && (
                            <Button
                              size="sm" variant="outline" disabled={isBusy}
                              className="border-[#062B1A]/15 text-[#062B1A]"
                              onClick={() => openAudience(automation)}
                            >
                              <Send className="mr-1.5 h-3.5 w-3.5" /> Run now
                            </Button>
                          )}
                          <Button
                            size="sm" variant="outline" disabled={isBusy}
                            className="border-[#062B1A]/15 text-[#062B1A]"
                            onClick={() => {
                              setRunAutomationId(automation.id);
                              setRunStatus('all');
                            }}
                          >
                            <History className="mr-1.5 h-3.5 w-3.5" /> History
                          </Button>
                          {automation.status !== 'archived' && (
                            <Button
                              size="sm" variant="outline" disabled={isBusy}
                              className="border-[#062B1A]/15 text-[#062B1A]/70"
                              onClick={() => changeStatus(automation, 'archived')}
                            >
                              <Archive className="mr-1.5 h-3.5 w-3.5" /> Archive
                            </Button>
                          )}
                          {/* Deletion is offered only where the server allows it:
                              a rule that has never contacted anybody. */}
                          {automation.status === 'draft' && !Object.keys(stats).length && (
                            <Button
                              size="sm" variant="ghost" disabled={isBusy}
                              className="text-red-700 hover:bg-red-50"
                              onClick={() => remove(automation)}
                            >
                              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4 space-y-4">
          <div className="flex flex-col gap-2 lg:flex-row">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#062B1A]/35" />
              <Input
                value={runSearch}
                onChange={(event) => setRunSearch(event.target.value)}
                placeholder="Search by name, email, reference or subject"
                aria-label="Search the automation history"
                className={`pl-9 ${inputClass}`}
              />
            </div>
            <Select value={runAutomationId} onValueChange={setRunAutomationId}>
              <SelectTrigger className={`lg:w-64 ${inputClass}`} aria-label="Filter by automation">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Every automation</SelectItem>
                {automations.map((automation) => (
                  <SelectItem key={automation.id} value={automation.id}>{automation.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={runStatus} onValueChange={setRunStatus}>
              <SelectTrigger className={`lg:w-48 ${inputClass}`} aria-label="Filter by outcome">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Every outcome</SelectItem>
                {RUN_STATUS_FILTERS.map((value) => (
                  <SelectItem key={value} value={value} className="capitalize">{value}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base text-[#062B1A]">
                <History className="h-4 w-4 text-[#D4AF37]" />
                Execution history
                {runs.total > 0 && (
                  <span className="text-sm font-normal text-[#062B1A]/50">
                    {runs.items.length} of {runs.total}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {runsLoading ? (
                <div className="flex items-center gap-2 py-8 text-sm text-[#062B1A]/55">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
                </div>
              ) : runs.items.length === 0 ? (
                <EmptyState
                  icon={Send}
                  title="Nothing here yet"
                  body="Every time an automation contacts somebody — or decides not to — it will be recorded here with the reason."
                />
              ) : (
                <div className="space-y-2">
                  {runs.items.map((run) => (
                    <div
                      key={run.id}
                      className="flex flex-col gap-3 rounded-xl border border-[#062B1A]/10 p-3 sm:flex-row sm:items-start sm:justify-between"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            variant="outline"
                            className={`text-[11px] ${RUN_STATUS_STYLES[run.status] || RUN_STATUS_STYLES.skipped}`}
                          >
                            {run.status === 'sent' && <CheckCircle2 className="mr-1 h-3 w-3" />}
                            {run.status === 'failed' && <XCircle className="mr-1 h-3 w-3" />}
                            {run.status === 'scheduled' && <Clock className="mr-1 h-3 w-3" />}
                            {run.status_label}
                          </Badge>
                          <span className="truncate font-medium text-[#062B1A]">
                            {run.contact_name || run.contact_email || 'A contact'}
                          </span>
                          {run.contact_reference && (
                            <span className="text-xs text-[#062B1A]/45">#{run.contact_reference}</span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-[#062B1A]/65">{run.outcome_message}</p>
                        <p className="mt-1 text-xs text-[#062B1A]/50">
                          <span className="font-medium">{run.automation_name}</span>
                          {run.trigger_reason ? ` — ${run.trigger_reason}` : ''}
                        </p>
                        {run.subject && (
                          <p className="mt-1 truncate text-xs text-[#062B1A]/45">Subject: {run.subject}</p>
                        )}
                        {run.missing_fields.length > 0 && (
                          <p className="mt-1 text-xs text-amber-700">
                            Blank in this message: {run.missing_fields.join(', ')}
                          </p>
                        )}
                        <p className="mt-1 text-xs text-[#062B1A]/40">
                          {run.status === 'scheduled'
                            ? `Sends ${formatDateTime(run.scheduled_for)}`
                            : formatDateTime(run.executed_at || run.created_at)}
                          {run.started_by_name ? ` · started by ${run.started_by_name}` : ''}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        {run.contact_link && (
                          <Button
                            size="sm" variant="outline"
                            className="border-[#062B1A]/15 text-[#062B1A]"
                            onClick={() => window.open(run.contact_link, '_blank', 'noopener')}
                          >
                            Open record
                          </Button>
                        )}
                        {run.can_retry && (
                          <Button
                            size="sm" variant="outline" disabled={busyRunId === run.id}
                            className="border-[#062B1A]/15 text-[#062B1A]"
                            onClick={() => retryRun(run)}
                          >
                            {busyRunId === run.id
                              ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                              : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />}
                            Retry
                          </Button>
                        )}
                        {run.can_cancel && (
                          <Button
                            size="sm" variant="ghost" disabled={busyRunId === run.id}
                            className="text-red-700 hover:bg-red-50"
                            onClick={() => cancelRun(run)}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog
        open={Boolean(audienceFor)}
        onOpenChange={(open) => { if (!open) { setAudienceFor(null); setAudience(null); } }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Run “{audienceFor?.name}” now</DialogTitle>
            <DialogDescription>
              This contacts everybody the automation&apos;s conditions select right now.
            </DialogDescription>
          </DialogHeader>

          {audienceLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-[#062B1A]/55">
              <Loader2 className="h-4 w-4 animate-spin" /> Working out who this would contact…
            </div>
          ) : audience ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-[#062B1A]/10 p-4">
                <p className="flex items-baseline gap-2">
                  <span className="text-3xl font-semibold text-[#062B1A]">{audience.will_contact}</span>
                  <span className="text-sm text-[#062B1A]/60">
                    {audience.will_contact === 1 ? 'person' : 'people'} will be emailed
                  </span>
                </p>
                <div className="mt-2 space-y-0.5 text-xs text-[#062B1A]/55">
                  <p>{audience.matched} {audience.matched === 1 ? 'record matches' : 'records match'} this automation.</p>
                  {audience.already_contacted > 0 && (
                    <p>{audience.already_contacted} already had this message and will be skipped.</p>
                  )}
                  {audience.missing_email > 0 && (
                    <p className="text-amber-700">
                      {audience.missing_email} {audience.missing_email === 1 ? 'has' : 'have'} no usable email address.
                    </p>
                  )}
                  {audience.truncated && (
                    <p className="text-amber-700">Only the most recent records were checked.</p>
                  )}
                </div>
              </div>

              {audience.sample.length > 0 && (
                <div>
                  <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#062B1A]/50">
                    <Users className="h-3.5 w-3.5" /> For example
                  </p>
                  <ul className="space-y-1 text-sm text-[#062B1A]/70">
                    {audience.sample.map((person) => (
                      <li key={person.email || person.name} className="flex flex-wrap gap-x-2">
                        <span className="font-medium text-[#062B1A]">{person.name}</span>
                        <span className="text-[#062B1A]/50">{person.email || 'no email on file'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!audience.email_configured && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Email sending is not configured, so nothing can be sent yet.
                </p>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              className="border-[#062B1A]/15"
              onClick={() => { setAudienceFor(null); setAudience(null); }}
            >
              Cancel
            </Button>
            <Button
              onClick={sendToAudience}
              disabled={sending || audienceLoading || !audience?.will_contact || !audience?.email_configured}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send to {audience?.will_contact ?? 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit automation' : 'New automation'}</DialogTitle>
            <DialogDescription>
              A new automation is saved as a draft. Nothing is sent until you activate it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="automation-name">Name</Label>
                <Input
                  id="automation-name"
                  value={form.name}
                  maxLength={120}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                  placeholder="Offer follow-up"
                  className={inputClass}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="automation-description">Description (optional)</Label>
                <Input
                  id="automation-description"
                  value={form.description}
                  maxLength={600}
                  onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                  placeholder="What this rule is for"
                  className={inputClass}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>1. What starts it</Label>
              <Select value={form.trigger_type} onValueChange={chooseTrigger}>
                <SelectTrigger className={inputClass}>
                  <SelectValue placeholder="Choose a trigger" />
                </SelectTrigger>
                <SelectContent>
                  {(catalog?.triggers || []).map((trigger) => (
                    <SelectItem key={trigger.key} value={trigger.key}>{trigger.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedTrigger && (
                <p className="text-xs text-[#062B1A]/50">{selectedTrigger.description}</p>
              )}
            </div>

            {selectedTrigger?.requires_audience && (
              <div className="space-y-1.5">
                <Label>Which records</Label>
                <Select value={form.audience} onValueChange={chooseAudience}>
                  <SelectTrigger className={inputClass}>
                    <SelectValue placeholder="Choose an audience" />
                  </SelectTrigger>
                  <SelectContent>
                    {(catalog?.audiences || []).map((item) => (
                      <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedTrigger?.supports_status_filter && selectedTrigger.status_options?.length > 0 && (
              <div className="space-y-1.5">
                <Label>Only for these statuses (optional)</Label>
                <div className="flex flex-wrap gap-3 rounded-lg border border-[#062B1A]/10 p-3">
                  {selectedTrigger.status_options.map((option) => (
                    <label key={option.value} className="flex items-center gap-2 text-sm text-[#062B1A]">
                      <Checkbox
                        checked={form.trigger_statuses.includes(option.value)}
                        onCheckedChange={() => toggleTriggerStatus(option.value)}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-[#062B1A]/50">Leave all unticked to run for any status.</p>
              </div>
            )}

            {selectedTrigger?.requires_wait_days && (
              <div className="space-y-1.5">
                <Label htmlFor="automation-wait">How long the record must stay untouched (days)</Label>
                <Input
                  id="automation-wait"
                  type="number" min={1} max={365}
                  value={form.wait_days}
                  onChange={(event) => setForm((current) => ({ ...current, wait_days: event.target.value }))}
                  className={`sm:w-40 ${inputClass}`}
                />
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>2. Conditions (optional)</Label>
                <Button
                  type="button" size="sm" variant="outline"
                  disabled={!formAudience || form.conditions.length >= (catalog?.max_conditions || 10)}
                  className="border-[#062B1A]/15 text-[#062B1A]"
                  onClick={addCondition}
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> Add condition
                </Button>
              </div>
              {!formAudience ? (
                <p className="text-xs text-[#062B1A]/50">Choose a trigger first.</p>
              ) : form.conditions.length === 0 ? (
                <p className="text-xs text-[#062B1A]/50">
                  No conditions — this runs for every {catalog?.audiences?.find((item) => item.value === formAudience)?.label?.toLowerCase() || 'record'} the trigger applies to.
                </p>
              ) : (
                <div className="space-y-2">
                  {form.conditions.map((condition, index) => {
                    const spec = conditionFields.find((field) => field.key === condition.field);
                    const operator = (catalog?.operators || []).find((item) => item.key === condition.operator);
                    const operators = (catalog?.operators || []).filter(
                      (item) => (spec?.temporal ? item.key === 'older_than_days' : item.key !== 'older_than_days'),
                    );
                    return (
                      <div key={index} className="flex flex-col gap-2 rounded-lg border border-[#062B1A]/10 p-2 sm:flex-row sm:items-center">
                        <Select value={condition.field} onValueChange={(value) => updateCondition(index, { field: value })}>
                          <SelectTrigger className={`sm:w-52 ${inputClass}`} aria-label="Condition field">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {conditionFields.map((field) => (
                              <SelectItem key={field.key} value={field.key}>{field.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={condition.operator} onValueChange={(value) => updateCondition(index, { operator: value })}>
                          <SelectTrigger className={`sm:w-44 ${inputClass}`} aria-label="Condition comparison">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {operators.map((item) => (
                              <SelectItem key={item.key} value={item.key}>{item.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {operator?.needs_value !== false && (
                          <Input
                            value={condition.value || ''}
                            onChange={(event) => updateCondition(index, { value: event.target.value })}
                            placeholder={spec?.temporal ? 'Days' : 'Value'}
                            aria-label="Condition value"
                            className={`flex-1 ${inputClass}`}
                          />
                        )}
                        <Button
                          type="button" size="icon" variant="ghost"
                          className="h-9 w-9 shrink-0 text-red-700"
                          aria-label="Remove this condition"
                          onClick={() => removeCondition(index)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                  <p className="text-xs text-[#062B1A]/50">Every condition must be true for the automation to run.</p>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>3. What it sends</Label>
              <Select
                value={form.template_id}
                onValueChange={(value) => setForm((current) => ({ ...current, template_id: value }))}
                disabled={!formAudience}
              >
                <SelectTrigger className={inputClass}>
                  <SelectValue placeholder={formAudience ? 'Choose an email template' : 'Choose a trigger first'} />
                </SelectTrigger>
                <SelectContent>
                  {availableTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formAudience && availableTemplates.length === 0 && (
                <p className="text-xs text-amber-700">
                  No email template is written for this audience yet. Create one in Email Templates first.
                </p>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="automation-sender">Send from</Label>
                <Select
                  value={form.sender || 'default'}
                  onValueChange={(value) => setForm((current) => ({ ...current, sender: value === 'default' ? '' : value }))}
                >
                  <SelectTrigger id="automation-sender" className={inputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default account</SelectItem>
                    {(catalog?.sender_accounts || []).map((account) => (
                      <SelectItem key={account.email} value={account.email}>{account.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="automation-delay">4. Wait before sending (hours)</Label>
                <Input
                  id="automation-delay"
                  type="number" min={0} max={catalog?.max_delay_hours || 720}
                  value={form.delay_hours}
                  onChange={(event) => setForm((current) => ({ ...current, delay_hours: event.target.value }))}
                  className={inputClass}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="automation-repeat">How often per person</Label>
                <Select
                  value={form.repeat_policy}
                  onValueChange={(value) => setForm((current) => ({ ...current, repeat_policy: value }))}
                  disabled={selectedTrigger?.kind === 'scheduled'}
                >
                  <SelectTrigger id="automation-repeat" className={inputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(catalog?.repeat_policies || []).map((item) => (
                      <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTrigger?.kind === 'scheduled' && (
                  <p className="text-xs text-[#062B1A]/50">
                    Time-based rules only ever contact each person once.
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)} className="border-[#062B1A]/15">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingId ? 'Save changes' : 'Create automation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
